import { useCallback, useEffect, useState } from "react";
import api, { formatDate } from "@/lib/api";
import { toast } from "sonner";
import {
  BarChart3, Home, TrendingUp, DollarSign, AlertTriangle, Wrench, CalendarClock,
  Download, Eye, PercentCircle, Lock, Repeat, Clock, FileText, BellRing,
} from "lucide-react";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const money2 = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const Stat = ({ icon: Icon, label, value, sub, tone = "forest", testid }) => (
  <div data-testid={testid} className="bg-white border border-line rounded-xl p-4">
    <div className="flex items-center gap-2 text-mute"><Icon className={`h-4 w-4 text-${tone}`} /><span className="text-xs font-medium">{label}</span></div>
    <p className="text-2xl font-heading font-semibold text-ink mt-1.5">{value}</p>
    {sub && <p className="text-xs text-mute mt-0.5">{sub}</p>}
  </div>
);

export const ReportsTab = ({ isPro, onUpgrade }) => {
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [port, setPort] = useState(null);
  const [fin, setFin] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async (yr) => {
    setLoading(true);
    try {
      const [p, f, a] = await Promise.all([
        api.get("/reports/portfolio"),
        api.get(`/reports/financial?year=${yr}`),
        api.get("/reports/analytics"),
      ]);
      setPort(p.data); setFin(f.data); setAnalytics(a.data); setLocked(false);
    } catch (e) {
      if (e.response?.status === 403) setLocked(true);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(year); }, [load, year]);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const { data } = await api.get(`/reports/financial/pdf?year=${year}`);
      const a = document.createElement("a");
      a.href = data.data_url; a.download = data.filename; a.click();
      toast.success("Financial report downloaded");
    } catch { toast.error("Could not export PDF"); }
    finally { setExporting(false); }
  };

  if (locked) {
    return (
      <div data-testid="reports-locked" className="max-w-xl mx-auto bg-white border border-line rounded-2xl p-8 text-center">
        <div className="w-12 h-12 rounded-xl bg-forest/10 flex items-center justify-center mx-auto"><Lock className="h-6 w-6 text-forest" /></div>
        <h2 className="text-xl font-heading font-medium text-ink mt-4">Reporting Suite is a Pro feature</h2>
        <p className="text-sm text-mute mt-2">Unlock your portfolio dashboard, financial reports with PDF export, and vacancy & maintenance analytics — the more your portfolio grows, the more it pays to see the full picture.</p>
        <ul className="text-sm text-ink text-left max-w-xs mx-auto mt-4 space-y-1.5">
          <li className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-forest" /> Occupancy & rent collection rate</li>
          <li className="flex items-center gap-2"><DollarSign className="h-4 w-4 text-forest" /> Monthly & annual income (PDF export)</li>
          <li className="flex items-center gap-2"><BarChart3 className="h-4 w-4 text-forest" /> Leasing & maintenance analytics</li>
        </ul>
        <button data-testid="reports-upgrade-btn" onClick={onUpgrade} className="mt-5 bg-forest text-white px-6 py-2.5 rounded-lg font-medium hover:bg-forest-hover transition-colors">Upgrade to Pro</button>
      </div>
    );
  }

  if (loading || !port || !fin || !analytics) {
    return <div data-testid="reports-loading" className="text-sm text-mute py-10 text-center">Loading reports…</div>;
  }

  const maxIncome = Math.max(...fin.monthly_income, 1);

  return (
    <div className="space-y-8" data-testid="reports-content">
      {/* Portfolio dashboard */}
      <section>
        <h2 className="text-lg font-heading font-medium text-ink flex items-center gap-2 mb-3"><Home className="h-5 w-5 text-forest" /> Portfolio Dashboard</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat testid="stat-units" icon={Home} label="Units managed" value={port.total_units} sub={`${port.occupied_units} occupied · ${port.vacant_units} vacant`} />
          <Stat testid="stat-occupancy" icon={PercentCircle} label="Occupancy" value={`${port.occupancy_pct}%`} sub={`${port.active_leases} active lease${port.active_leases === 1 ? "" : "s"}`} />
          <Stat testid="stat-collection" icon={TrendingUp} label="Collection rate" value={`${port.collection_rate}%`} sub={`${port.collected_this_period} collected this month`} />
          <Stat testid="stat-outstanding" icon={AlertTriangle} label="Outstanding rent" value={money(port.outstanding_rent)} sub={port.accrued_platform_fees ? `${money2(port.accrued_platform_fees)} fees accrued` : "Current period"} tone="terracotta" />
        </div>
        <div className="grid md:grid-cols-2 gap-3 mt-3">
          <div className="bg-white border border-line rounded-xl p-4">
            <p className="text-sm font-medium text-ink flex items-center gap-2 mb-2"><Wrench className="h-4 w-4 text-forest" /> Maintenance summary</p>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="rounded-full border border-amber-200 bg-amber-50 text-amber-700 px-2.5 py-1">Open {port.maintenance.open}</span>
              <span className="rounded-full border border-blue-200 bg-blue-50 text-blue-700 px-2.5 py-1">In progress {port.maintenance.in_progress}</span>
              <span className="rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 px-2.5 py-1">Resolved {port.maintenance.resolved}</span>
              {port.maintenance.urgent_open > 0 && <span className="rounded-full border border-red-200 bg-red-50 text-red-700 px-2.5 py-1">Urgent {port.maintenance.urgent_open}</span>}
            </div>
          </div>
          <div className="bg-white border border-line rounded-xl p-4">
            <p className="text-sm font-medium text-ink flex items-center gap-2 mb-2"><CalendarClock className="h-4 w-4 text-forest" /> Lease expiration calendar{port.expiring_soon_count > 0 && <span data-testid="expiring-soon-count" className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">{port.expiring_soon_count} expiring ≤60d</span>}</p>
            {port.expirations.length === 0 ? <p className="text-xs text-mute">No leases expiring in the next 120 days.</p> : (
              <div className="space-y-1.5">
                {port.expirations.slice(0, 6).map((e) => (
                  <div key={`${e.property}-${e.tenant || ""}-${e.days_left}`} data-testid="expiration-row" className="flex items-center justify-between gap-2 text-xs">
                    <span className="text-ink truncate flex items-center gap-1.5">
                      {e.property}{e.tenant ? ` · ${e.tenant}` : ""}
                      {e.expiring_soon && <span data-testid="expiring-soon-badge" className="text-[9px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5 uppercase">Soon</span>}
                      {e.reminder_sent && <BellRing className="h-3 w-3 text-forest" title="Reminder sent" />}
                    </span>
                    <span className={`shrink-0 font-medium ${e.days_left < 0 ? "text-red-600" : e.days_left <= 30 ? "text-amber-600" : e.expiring_soon ? "text-amber-500" : "text-mute"}`}>{e.days_left < 0 ? `${-e.days_left}d overdue` : `${e.days_left}d`}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Financial */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-heading font-medium text-ink flex items-center gap-2"><DollarSign className="h-5 w-5 text-forest" /> Financial Reports</h2>
          <div className="flex items-center gap-2">
            <select data-testid="report-year" value={year} onChange={(e) => setYear(Number(e.target.value))} className="border border-line rounded-lg px-3 py-1.5 text-sm">
              {[0, 1, 2].map((n) => { const y = new Date().getFullYear() - n; return <option key={y} value={y}>{y}</option>; })}
            </select>
            <button data-testid="export-pdf-btn" disabled={exporting} onClick={exportPdf} className="inline-flex items-center gap-1.5 bg-forest text-white px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-forest-hover disabled:opacity-60"><Download className="h-4 w-4" /> Export PDF</button>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-3 mb-3">
          <Stat testid="stat-annual" icon={DollarSign} label={`${year} rent income`} value={money(fin.annual_total)} />
          <Stat testid="stat-deposit" icon={FileText} label="Deposit liability" value={money(fin.deposit_liability)} sub="Held on active leases" />
          <Stat testid="stat-delinquency" icon={AlertTriangle} label="Delinquent accounts" value={fin.delinquency.length} sub="Current period" tone="terracotta" />
        </div>
        <div className="bg-white border border-line rounded-xl p-4">
          <p className="text-sm font-medium text-ink mb-3">Monthly rent income · {year}</p>
          <div className="flex items-end gap-1.5 h-32">
            {fin.monthly_income.map((v, i) => (
              <div key={MONTHS[i]} className="flex-1 flex flex-col items-center gap-1 group">
                <div className="w-full bg-forest/15 rounded-t relative flex items-end" style={{ height: "100%" }}>
                  <div className="w-full bg-forest rounded-t transition-all" style={{ height: `${(v / maxIncome) * 100}%` }} title={money2(v)} />
                </div>
                <span className="text-[9px] text-mute">{MONTHS[i]}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-3 mt-3">
          <div className="bg-white border border-line rounded-xl p-4">
            <p className="text-sm font-medium text-ink mb-2">Income by property</p>
            {fin.per_property.length === 0 ? <p className="text-xs text-mute">No income recorded for {year}.</p> : (
              <div className="space-y-1.5">{fin.per_property.slice(0, 6).map((p) => (
                <div key={p.property} className="flex justify-between text-sm"><span className="text-ink truncate">{p.property}</span><span className="font-medium text-ink tabular-nums">{money2(p.amount)}</span></div>
              ))}</div>
            )}
          </div>
          <div className="bg-white border border-line rounded-xl p-4">
            <p className="text-sm font-medium text-ink mb-2">Delinquency</p>
            {fin.delinquency.length === 0 ? <p className="text-xs text-mute">No delinquent accounts this period. 🎉</p> : (
              <div className="space-y-1.5">{fin.delinquency.map((d) => (
                <div key={`${d.property}-${d.tenant || ""}`} data-testid="delinquency-row" className="flex justify-between text-sm"><span className="text-ink truncate">{d.property}{d.tenant ? ` · ${d.tenant}` : ""}</span><span className="font-medium text-terracotta tabular-nums">{money2(d.amount_due)}</span></div>
              ))}</div>
            )}
          </div>
        </div>
        <div className="bg-white border border-line rounded-xl p-4 mt-3">
          <p className="text-sm font-medium text-ink mb-2">Payment history</p>
          {fin.payment_history.length === 0 ? <p className="text-xs text-mute">No payments recorded.</p> : (
            <div className="divide-y divide-line">{fin.payment_history.slice(0, 8).map((h) => (
              <div key={`${h.property || "rent"}-${h.date}-${h.amount}`} data-testid="payment-row" className="flex items-center justify-between py-2 text-sm">
                <div><span className="text-ink">{h.property || "Rent"}</span><span className="text-xs text-mute ml-2 capitalize">{h.method}</span></div>
                <div className="flex items-center gap-3"><span className="text-xs text-mute">{formatDate(h.date)}</span><span className="font-medium text-ink tabular-nums">{money2(h.amount)}</span></div>
              </div>
            ))}</div>
          )}
        </div>
      </section>

      {/* Analytics */}
      <section>
        <h2 className="text-lg font-heading font-medium text-ink flex items-center gap-2 mb-3"><BarChart3 className="h-5 w-5 text-forest" /> Vacancy, Leasing & Maintenance Analytics</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat testid="stat-vacant-days" icon={Clock} label="Avg days vacant" value={analytics.avg_days_vacant} sub="List → leased" />
          <Stat testid="stat-app-conv" icon={TrendingUp} label="Application conversion" value={`${analytics.application_conversion}%`} sub={`${analytics.approved_applications}/${analytics.total_applications} approved`} />
          <Stat testid="stat-showing-lease" icon={PercentCircle} label="Showing → lease" value={`${analytics.showing_to_lease_ratio}%`} sub={`${analytics.completed_showings} showings`} />
          <Stat testid="stat-maint-completion" icon={Wrench} label="Avg repair time" value={`${analytics.maintenance.avg_completion_days}d`} sub={`${analytics.maintenance.open} open`} />
        </div>
        <div className="grid md:grid-cols-2 gap-3 mt-3">
          <div className="bg-white border border-line rounded-xl p-4">
            <p className="text-sm font-medium text-ink flex items-center gap-2 mb-2"><Eye className="h-4 w-4 text-forest" /> Most viewed properties</p>
            {analytics.most_viewed.length === 0 ? <p className="text-xs text-mute">No listing views recorded yet.</p> : (
              <div className="space-y-1.5">{analytics.most_viewed.map((p) => (
                <div key={p.property} className="flex justify-between text-sm"><span className="text-ink truncate">{p.property}</span><span className="font-medium text-ink">{p.views} views</span></div>
              ))}</div>
            )}
          </div>
          <div className="bg-white border border-line rounded-xl p-4">
            <p className="text-sm font-medium text-ink flex items-center gap-2 mb-2"><Repeat className="h-4 w-4 text-forest" /> Recurring maintenance issues</p>
            {analytics.maintenance.recurring_issues.length === 0 ? <p className="text-xs text-mute">No recurring issues detected.</p> : (
              <div className="space-y-1.5">{analytics.maintenance.recurring_issues.map((r) => (
                <div key={r.issue} className="flex justify-between text-sm"><span className="text-ink truncate">{r.issue}</span><span className="font-medium text-amber-600">×{r.count}</span></div>
              ))}</div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};
